/**
 * Agent Knowledge Sources List Component
 * Displays knowledge sources linked to an agent with add and delete actions
 */

import {
  Stack,
  Title,
  Card,
  Icon,
  useToast,
  useActions,
  useTheme,
  P,
  ActionMenu,
  Accordion
} from "@introist/react-foundation/v2";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { agentsApi, AgentKnowledgeSource } from "../api";
import { useAgentKnowledgeSources } from "../hooks/use-agent-knowledge-sources";
import { useKnowledgeSourcesWorkspace } from "../hooks/use-knowledge-sources-workspace";
import { Skeletons } from "../../components/molecules";
import { IconButtonSelect } from "../../foundation/blocks/IconButtonSelect";
import { getKnowledgeSourceIcon } from "./KnowledgeSourceIcon";
import { useMemo } from "react";
import { SelectorIcon } from "../../modules/employees/fields/EmployeeFieldSelector";

interface AgentKnowledgeSourcesListProps {
  agentConfigId: string;
}

export const AgentKnowledgeSourcesList = ({ agentConfigId }: AgentKnowledgeSourcesListProps) => {
  const { data: agentKnowledgeSources = [], isLoading } = useAgentKnowledgeSources(agentConfigId);
  const { data: allKnowledgeSources = [] } = useKnowledgeSourcesWorkspace();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { onConfirmAction } = useActions();
  const { theme } = useTheme();

  // Get available knowledge sources (not yet linked to this agent)
  const availableKnowledgeSources = useMemo(() => {
    const linkedSourceIds = new Set(agentKnowledgeSources.map(s => s.id));
    return allKnowledgeSources.filter(source => !linkedSourceIds.has(source.id));
  }, [agentKnowledgeSources, allKnowledgeSources]);

  const addKnowledgeSourceMutation = useMutation({
    mutationFn: async (knowledgeSourceId: string) => {
      return agentsApi.addKnowledgeSource(agentConfigId, { knowledgeSourceId });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-studio", "agents", agentConfigId, "knowledge"]
      });
      toast.success("Knowledge source added successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to add knowledge source: ${error.message}`);
    }
  });

  const removeKnowledgeSourceMutation = useMutation({
    mutationFn: async (knowledgeSourceId: string) => {
      return agentsApi.removeKnowledgeSource(agentConfigId, knowledgeSourceId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["ai-studio", "agents", agentConfigId, "knowledge"]
      });
      toast.success("Knowledge source removed successfully");
    },
    onError: (error: any) => {
      toast.error(`Failed to remove knowledge source: ${error.message}`);
    }
  });

  const handleAddKnowledgeSource = async (knowledgeSourceId: string) => {
    await addKnowledgeSourceMutation.mutateAsync(knowledgeSourceId);
  };

  const handleRemove = (source: AgentKnowledgeSource) => {
    return onConfirmAction(
      async () => {
        await removeKnowledgeSourceMutation.mutateAsync(source.id);
      },
      {
        title: "Remove knowledge source",
        description: `Are you sure you want to remove ${source.name} from this agent?`,
        confirmTitle: "Remove"
      }
    );
  };

  if (isLoading) {
    return <Skeletons.List rowCount={2} rowHeight={80} />;
  }

  return (
    <Accordion
      title="Knowledge"
      description={
        agentKnowledgeSources.length > 0
          ? agentKnowledgeSources.map(k => k.name).join(",")
          : "Documents and data sources to reference"
      }
      icon={<SelectorIcon icon="book" />}
      defaultOpen={false}
      compact
      toggleOpenOnHeaderClick
    >
      <Stack vertical style={{ gap: "var(--spacing-small)" }}>
        {availableKnowledgeSources.length > 0 && (
          <Stack style={{ justifyContent: "flex-end" }}>
            <IconButtonSelect
              icon="plus"
              variant="blended"
              size="small"
              onChange={handleAddKnowledgeSource}
              options={availableKnowledgeSources.map(source => ({
                key: source.id,
                title: source.name,
                startAdornment: (
                  <div style={{ width: "20px", height: "20px" }}>
                    {getKnowledgeSourceIcon(source.type)}
                  </div>
                )
              }))}
            />
          </Stack>
        )}

        {agentKnowledgeSources.length === 0 ? (
          <Card>
            <Stack
              vertical
              style={{
                alignItems: "center",
                gap: "var(--spacing-small)",
                padding: "var(--spacing-medium)"
              }}
            >
              <P style={{ color: "var(--palette-foreground-subdued)" }}>
                No knowledge sources linked yet
              </P>
            </Stack>
          </Card>
        ) : (
          <Stack vertical style={{ gap: "var(--spacing-small)" }}>
            {agentKnowledgeSources.map(source => (
              <Card key={source.id}>
                <Stack
                  style={{
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    {getKnowledgeSourceIcon(source.type)}
                  </div>
                  <Stack
                    vertical
                    style={{
                      gap: "var(--spacing-xxSmall)",
                      flex: 1,
                      marginLeft: "var(--spacing-medium)"
                    }}
                  >
                    <Title variant="bold">{source.name}</Title>
                  </Stack>
                  <ActionMenu
                    options={[
                      {
                        key: "remove",
                        title: "Remove",
                        onClick: handleRemove(source),
                        startAdornment: <Icon name="trash" color={theme.palette.danger.default} />,
                        titleStyles: { color: "var(--palette-danger-default)" }
                      }
                    ]}
                  />
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>
    </Accordion>
  );
};
